import { PaletteColorPicker } from "@/components/cms/theme/PaletteColorPicker";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { CmsColor } from "@/lib/cms/cms-theme.constants";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  colors: CmsColor[];
  pageBackgroundColorId: string | null | undefined;
  defaultPageBackgroundHex: string;
  defaultPageBackgroundName?: string;
  onChange: (pageBackgroundColorId: string | null) => void;
  onColorsChange?: (colors: CmsColor[]) => void;
};

export function PageBackgroundField({
  colors,
  pageBackgroundColorId,
  defaultPageBackgroundHex,
  defaultPageBackgroundName,
  onChange,
  onColorsChange,
}: Props) {
  const { t } = useTranslation();
  const [showCustomPicker, setShowCustomPicker] = useState(
    pageBackgroundColorId != null,
  );
  const useDefault = pageBackgroundColorId == null && !showCustomPicker;

  useEffect(() => {
    if (pageBackgroundColorId != null) {
      setShowCustomPicker(true);
    }
  }, [pageBackgroundColorId]);

  return (
    <div className="rounded-lg border border-slate-200 bg-transparent p-4 space-y-4">
      <h4 className="text-sm font-semibold text-slate-800">
        {t("cms.page.settings.backgroundTitle")}
      </h4>
      <RadioGroup
        value={useDefault ? "default" : "custom"}
        onValueChange={(v) => {
          if (v === "default") {
            setShowCustomPicker(false);
            onChange(null);
            return;
          }

          setShowCustomPicker(true);
          onChange(colors[0]?.id ?? null);
        }}
        className="flex flex-col gap-2"
      >
        <div className="flex items-start gap-2">
          <RadioGroupItem value="default" id="page-bg-default" className="mt-1" />
          <div className="flex flex-col gap-1">
            <Label
              htmlFor="page-bg-default"
              className="font-normal cursor-pointer"
            >
              {t("cms.appearance.pageBackground.useDefault")}
            </Label>
            {useDefault && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span
                  className="h-6 w-6 shrink-0 rounded border border-slate-200"
                  style={{ backgroundColor: defaultPageBackgroundHex }}
                  aria-hidden
                />
                <span>
                  {t("cms.page.settings.defaultBackgroundPreview", {
                    name:
                      defaultPageBackgroundName ??
                      t("cms.appearance.pageBackground.defaultName"),
                    hex: defaultPageBackgroundHex,
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="custom" id="page-bg-custom" />
          <Label htmlFor="page-bg-custom" className="font-normal cursor-pointer">
            {t("cms.appearance.pageBackground.custom")}
          </Label>
        </div>
      </RadioGroup>
      {showCustomPicker && (
        <PaletteColorPicker
          label={t("cms.appearance.pageBackground.color")}
          colors={colors}
          value={pageBackgroundColorId}
          onChange={onChange}
          onColorsChange={onColorsChange}
        />
      )}
    </div>
  );
}
